/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import { compose } from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';
import waitForProps from 'js/lib/waitForProps';

import { withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import {
  loadSessionsForCourse,
  loadSessionsForUnprivileged,
  selectAll,
  selectOnDemand,
  selectSession,
} from 'bundles/discussions/actions/SessionFilterActions';
import SessionFilterDropdown from 'bundles/discussions/components/personalization-sessionFilter/SessionFilterDropdown';
import Icon from 'bundles/iconfont/Icon';

import _t from 'i18n!nls/discussions';

type Props = {
  courseId: string;
  loaded: boolean;
  currentSessionId: string;
  discussionsLandingPageActive: boolean;
  containsMultipleBranches: boolean;
  hasModerationRole: boolean;
  isPrivateSession: boolean;
  forceLoad: boolean;
  isSessionsV2Enabled: boolean;
  theme: Theme;
};

class SessionFilter extends React.Component<Props> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof SessionFilter.contextTypes>;

  static defaultProps = {
    forceLoad: false,
  };

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    if (!props.loaded && props.hasModerationRole) {
      context.executeAction(loadSessionsForCourse, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }

    if (!props.loaded && !props.hasModerationRole) {
      context.executeAction(loadSessionsForUnprivileged, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }
  }

  handleDropdownChange = (filterId: $TSFixMe, branchId: $TSFixMe) => {
    if (filterId === 'ondemand') {
      this.context.executeAction(selectOnDemand);
    } else if (filterId === 'all') {
      this.context.executeAction(selectAll, { branchId });
    } else {
      this.context.executeAction(selectSession, { sessionId: filterId });
    }
  };

  render() {
    const { discussionsLandingPageActive, loaded, hasModerationRole, isSessionsV2Enabled } = this.props;

    if (!discussionsLandingPageActive || !loaded || !hasModerationRole) {
      return null;
    }

    const tooltipText = _t(
      `Instructors can now change the structure of the discussion forums for each version of the course to better
  fit the material. It is still possible to view all discussions in a particular version, but no longer
  possible to view discussions across every session.`
    );

    const tooltipId = 'session-filter-tooltip';

    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    return (
      <div
        className="rc-personalization_SessionFilter card-rich-interaction"
        css={css`
          padding: var(--cds-spacing-300);
          margin: 0 0 var(--cds-spacing-300) 0;
        `}
      >
        <div>
          <div className="horizontal-box align-items-spacebetween">
            <div>
              <h2 className="label-text color-secondary-text title">
                <Icon name="lock" />
                &nbsp;
                {isSessionsV2Enabled ? _t('Filter by version and cohort') : _t('Filter by session')}
              </h2>
            </div>
            {this.props.containsMultipleBranches ? (
              <TooltipWrapper tooltipId={tooltipId} placement="bottom" message={tooltipText}>
                <div
                  className="label-text color-secondary-text title"
                  role="tooltip"
                  tabIndex={0}
                  aria-label={tooltipText}
                >
                  <Icon name="question-circle-o" className="cif-lg" />
                </div>
              </TooltipWrapper>
            ) : null}
          </div>

          <p className="caption-text color-secondary-text align-left">{_t('Visible to staff and mentors')}</p>

          <SessionFilterDropdown
            isSessionsV2Enabled={isSessionsV2Enabled}
            handleDropdownChange={this.handleDropdownChange}
          />
        </div>
      </div>
    );
  }
}

export default compose(
  connectToStores(
    ['SessionFilterStore', 'SessionStore', 'CourseStore', 'CourseMembershipStore', 'ApplicationStore'],
    ({ SessionFilterStore, SessionStore, CourseStore, CourseMembershipStore, ApplicationStore }) => {
      return {
        hasModerationRole: CourseMembershipStore.hasModerationRole() || ApplicationStore.isSuperuser(),
        loaded: SessionFilterStore.filtersLoaded,
        containsMultipleBranches: !!SessionFilterStore.containsMultipleBranches,
        currentSessionId: SessionStore.isEnrolled() ? SessionStore.getSessionId() : undefined,
        courseSlug: CourseStore.getCourseSlug(),
        courseId: CourseStore.getCourseId(),
        isPrivateSession: SessionStore.isEnrolled() ? SessionStore.isSessionPrivate() : false,
      };
    }
  ),
  waitForProps(['courseId']),
  waitForGraphQL(
    gql`
      query PersonalizationSessionsV2AuthoringEligibilityCheckQuery($courseId: String!) {
        SessionsV2AuthoringEligibilityCheckV1Resource {
          get(id: $courseId) {
            eligibleForSessionsV2
          }
        }
      }
    `,
    {
      options: ({ courseId }: { courseId: string }) => ({
        variables: {
          courseId,
        },
      }),
      props: ({ data }: any) => {
        const { SessionsV2AuthoringEligibilityCheckV1Resource } = data;
        if (SessionsV2AuthoringEligibilityCheckV1Resource) {
          const { get } = SessionsV2AuthoringEligibilityCheckV1Resource;
          return {
            isSessionsV2Enabled: get && get.eligibleForSessionsV2,
          };
        }
        return {
          isSessionsV2Enabled: false,
        };
      },
    }
  ),
  connectToRouter((router, props: { forceLoad: any; courseSlug: string }) => ({
    discussionsLandingPageActive:
      props.forceLoad ||
      router.isActive(
        {
          pathname: `/learn/${props.courseSlug}/discussions`,
          query: router.location.query,
        },
        true
      ) ||
      router.isActive(
        {
          pathname: `/learn/${props.courseSlug}/discussions/all`,
          query: router.location.query,
        },
        true
      ) ||
      router.isActive(
        {
          pathname: `/learn/${props.courseSlug}/discussions/tab/foryou`,
          query: router.location.query,
        },
        true
      ) ||
      router.isActive(
        {
          pathname: `/learn/${props.courseSlug}/discussions/tab/all`,
          query: router.location.query,
        },
        true
      ) ||
      router.isActive(
        {
          pathname: `/learn/${props.courseSlug}/discussions/tab/myposts`,
          query: router.location.query,
        },
        true
      ),
  }))
)(withTheme(SessionFilter));
